import {
  $isElementNode,
  ParagraphNode,
  type DOMConversionMap,
  type DOMExportOutput,
  type EditorConfig,
  type LexicalEditor,
  type LexicalNode,
  type SerializedParagraphNode,
} from 'lexical';

/**
 * Block-level spacing properties this node round-trips. Lexical's built-in
 * ParagraphNode only carries `text-align` across DOM import/export/render — it
 * drops arbitrary inline block style. Without this drop-in replacement, a
 * paragraph's line spacing renders while you edit but vanishes on save/reopen
 * (and never reaches the sent email HTML), exactly like the text-color problem
 * that ExtendedTextNode solves for inline text.
 *
 * margin-top/bottom are included so spacing on pasted/imported content is
 * preserved too, even though the toolbar control only sets line-height.
 */
const SPACING_PROPS = ['line-height', 'margin-top', 'margin-bottom'] as const;

/** Copy just the spacing props from a style string onto a DOM element. */
function syncSpacing(dom: HTMLElement, styleText: string): void {
  const parsed = document.createElement('div');
  parsed.style.cssText = styleText;
  for (const prop of SPACING_PROPS) {
    const value = parsed.style.getPropertyValue(prop);
    if (value) dom.style.setProperty(prop, value);
    else dom.style.removeProperty(prop);
  }
}

/** Pull the spacing props off an imported element into a style string. */
function extractSpacing(element: HTMLElement): string {
  return SPACING_PROPS.map((prop) => {
    const value = element.style.getPropertyValue(prop);
    return value ? `${prop}: ${value}` : null;
  })
    .filter((part): part is string => part !== null)
    .join('; ');
}

export class ExtendedParagraphNode extends ParagraphNode {
  static getType(): string {
    return 'extended-paragraph';
  }

  static clone(node: ExtendedParagraphNode): ExtendedParagraphNode {
    return new ExtendedParagraphNode(node.__key);
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    syncSpacing(dom, this.__style);
    return dom;
  }

  updateDOM(
    prevNode: ExtendedParagraphNode,
    dom: HTMLElement,
    config: EditorConfig,
  ): boolean {
    // Reflect spacing changes in place (setProperty leaves text-align, which
    // the core reconciler owns, untouched) without forcing a DOM rebuild.
    if (prevNode.__style !== this.__style) syncSpacing(dom, this.__style);
    return super.updateDOM(prevNode, dom, config);
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const output = super.exportDOM(editor);
    if (output.element instanceof HTMLElement && this.__style) {
      syncSpacing(output.element, this.__style);
    }
    return output;
  }

  // Wrap the built-in `p` converter so spacing survives import. Bumped one
  // priority above the default (0) so this wins.
  static importDOM(): DOMConversionMap | null {
    const base = ParagraphNode.importDOM();
    const baseP = base?.p;
    if (!baseP) return base;
    return {
      p: (node) => {
        const entry = baseP(node);
        if (!entry) return null;
        const originalConversion = entry.conversion;
        return {
          conversion: (element) => {
            const result = originalConversion(element);
            if (
              result?.node &&
              !Array.isArray(result.node) &&
              $isElementNode(result.node) &&
              element instanceof HTMLElement
            ) {
              const spacing = extractSpacing(element);
              if (spacing) result.node.setStyle(spacing);
            }
            return result;
          },
          priority: 1,
        };
      },
    };
  }

  static importJSON(
    serializedNode: SerializedParagraphNode,
  ): ExtendedParagraphNode {
    return new ExtendedParagraphNode().updateFromJSON(serializedNode);
  }

  exportJSON(): SerializedParagraphNode {
    return { ...super.exportJSON(), type: 'extended-paragraph', version: 1 };
  }
}

export function $isExtendedParagraphNode(
  node: LexicalNode | null | undefined,
): node is ExtendedParagraphNode {
  return node instanceof ExtendedParagraphNode;
}
